import React, { createContext, useContext, useState, useCallback } from 'react';
import { SubscriptionPlan } from '@/services/billing';

export type FeatureKey =
  | 'automation'
  | 'creatorAccounts'
  | 'teamManagement'
  | 'premiumThemes'
  | 'customBranding'
  | 'customDomain'
  | 'advancedVideoProofing'
  // PLANS-3 — video ladder. Starter is photo galleries only;
  // 1080p proofing + delivery unlocks at Orbit; 4K streaming +
  // original-file downloads live at Business.
  | 'videoDelivery'
  | 'videoOriginals'
  | 'portalCustomization'
  | 'apiAccess'
  | 'prioritySupport'
  | 'unlimitedProjects'
  | 'unlimitedClients'
  | 'productionManagement'
  | 'extraStorage'
  // SITES-3 — Nova Design (she builds the whole site from a sentence).
  // Templates stay on every plan; this is the middle-tier unlock.
  | 'novaSites'
  // Intake form features
  | 'intakeForms'
  | 'intakeFormLimit'
  | 'intakeFormCustomFields'
  | 'intakeFormLogic'
  | 'intakeFormTemplates'
  | 'intakeFormAutomation'
  | 'intakeFormAI';

// PLANS-1 — feature → minimum plan mapping. The Starter tier ships
// the core CRM (portal, intake, delivery, payments) but no custom
// branding, custom domain, or team surfaces. Everything at Orbit
// unlocks the full solo experience; team + advanced Nova features
// live at Business.
export const FEATURE_REQUIRED_PLAN: Record<FeatureKey, SubscriptionPlan> = {
  automation: 'orbit_starter',           // basic on Starter, advanced on Orbit+
  premiumThemes: 'orbit',                // custom branding drives theme access
  customBranding: 'orbit',               // Starter is Orbit-branded
  customDomain: 'orbit',                 // Starter cannot swap the domain
  advancedVideoProofing: 'orbit',        // PLANS-3 — Starter has no video at all now
  videoDelivery: 'orbit',                // PLANS-3 — Starter is photo galleries only
  videoOriginals: 'orbit_business',      // PLANS-3 — 4K + original files
  portalCustomization: 'orbit',
  prioritySupport: 'orbit_business',
  creatorAccounts: 'orbit_business',
  teamManagement: 'orbit_business',
  apiAccess: 'orbit_business',
  productionManagement: 'orbit_business',
  unlimitedProjects: 'orbit',            // Starter caps at 5 active projects
  unlimitedClients: 'orbit',             // Starter caps at 15 clients
  extraStorage: 'orbit',                 // Orbit adds another 500 GB
  novaSites: 'orbit',                    // base sees it, greyed out
  // Intake form features
  intakeForms: 'orbit_starter',
  intakeFormLimit: 'orbit_starter',      // unlimited on all paid plans
  intakeFormCustomFields: 'orbit_starter',
  intakeFormLogic: 'orbit_starter',
  intakeFormTemplates: 'orbit_starter',
  intakeFormAutomation: 'orbit_starter', // basic on Starter, advanced on Orbit+
  intakeFormAI: 'orbit_business',
};

// Human-readable feature names
export const FEATURE_NAMES: Record<FeatureKey, string> = {
  automation: 'Automation Rules',
  creatorAccounts: 'Creator Accounts',
  teamManagement: 'Team Management',
  premiumThemes: 'Premium Themes',
  customBranding: 'Custom Branding',
  customDomain: 'Custom Domain',
  advancedVideoProofing: 'Full Video Proofing',
  videoDelivery: 'Video Delivery',
  videoOriginals: '4K + Original Files',
  portalCustomization: 'Portal Customization',
  apiAccess: 'API Access',
  prioritySupport: 'Priority Support',
  unlimitedProjects: 'Unlimited Projects',
  unlimitedClients: 'Unlimited Clients',
  productionManagement: 'Production Management',
  extraStorage: 'Extended Storage',
  novaSites: 'Nova Design',
  // Intake form features
  intakeForms: 'Intake Forms',
  intakeFormLimit: 'Multiple Intake Forms',
  intakeFormCustomFields: 'Custom Form Fields',
  intakeFormLogic: 'Conditional Logic',
  intakeFormTemplates: 'Form Templates',
  intakeFormAutomation: 'Form Automation',
  intakeFormAI: 'AI Form Insights',
};

// Feature descriptions
export const FEATURE_DESCRIPTIONS: Record<FeatureKey, string> = {
  // P1-WEB-1 — copy narrowed to what AutomationSettings actually
  // does (rules that fire on inquiries, project status changes,
  // deliveries, milestones). The old "workflows" phrasing promised
  // the killed Workflows builder surface (#341).
  automation: 'Send emails and team pings automatically when inquiries land, projects change, or deliveries go out.',
  creatorAccounts: 'Bring external creators and collaborators into your projects.',
  teamManagement: 'Add team members with role-based permissions.',
  premiumThemes: 'Access every planetary theme, including Andromeda, Earth, and beyond.',
  customBranding: 'Put your logo across the app and every client email.',
  customDomain: 'Serve your Sites from your own domain.',
  advancedVideoProofing: 'Run unlimited concurrent video reviews with pro playback controls.',
  videoDelivery:
    'Send finished films the way you send galleries — streamed in 1080p, with client notes pinned to the frame.',
  videoOriginals:
    'Deliver in 4K and hand clients the original files, straight from the gallery.',
  portalCustomization: 'Customize the client portal experience.',
  apiAccess: 'Access the API for custom integrations.',
  prioritySupport: 'Get priority access to support and new features.',
  unlimitedProjects: 'Run as many active projects as your work needs.',
  unlimitedClients: 'Add every past, present, and future client.',
  productionManagement: 'Track shoots, deliveries, and team assignments from one board.',
  extraStorage: 'Add more storage as your galleries and video libraries grow.',
  novaSites: 'Tell Nova about your work in a sentence. She writes the words, picks the look, and builds the pages.',
  // Intake form features
  intakeForms: 'Create intake forms to capture client inquiries.',
  intakeFormLimit: 'Create multiple intake forms for different services.',
  intakeFormCustomFields: 'Add custom fields to your intake forms.',
  intakeFormLogic: 'Show/hide fields based on previous answers.',
  intakeFormTemplates: 'Start from a pre-built template and be collecting leads in minutes.',
  intakeFormAutomation: 'Automatically create projects from form submissions.',
  intakeFormAI: 'Get AI-powered insights on form submissions.',
};

interface UpgradeModalState {
  isOpen: boolean;
  feature: FeatureKey | null;
  requiredPlan: SubscriptionPlan | null;
}

interface UpgradeModalContextType {
  state: UpgradeModalState;
  openUpgradeModal: (feature: FeatureKey) => void;
  closeUpgradeModal: () => void;
  requireFeature: (feature: FeatureKey, currentPlan: SubscriptionPlan) => boolean;
}

const UpgradeModalContext = createContext<UpgradeModalContextType | null>(null);

export const UpgradeModalProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [state, setState] = useState<UpgradeModalState>({
    isOpen: false,
    feature: null,
    requiredPlan: null,
  });

  const openUpgradeModal = useCallback((feature: FeatureKey) => {
    const requiredPlan = FEATURE_REQUIRED_PLAN[feature];
    setState({
      isOpen: true,
      feature,
      requiredPlan,
    });
  }, []);

  const closeUpgradeModal = useCallback(() => {
    setState({
      isOpen: false,
      feature: null,
      requiredPlan: null,
    });
  }, []);

  // Helper to check if feature is available and show modal if not
  const requireFeature = useCallback((feature: FeatureKey, currentPlan: SubscriptionPlan): boolean => {
    const requiredPlan = FEATURE_REQUIRED_PLAN[feature];
    const planOrder: SubscriptionPlan[] = ['free', 'orbit_starter', 'orbit', 'orbit_business'];
    const currentIndex = planOrder.indexOf(currentPlan);
    const requiredIndex = planOrder.indexOf(requiredPlan);

    if (currentIndex >= requiredIndex) {
      return true; // Feature is available
    }

    // Feature not available, show upgrade modal
    openUpgradeModal(feature);
    return false;
  }, [openUpgradeModal]);

  return (
    <UpgradeModalContext.Provider value={{ state, openUpgradeModal, closeUpgradeModal, requireFeature }}>
      {children}
    </UpgradeModalContext.Provider>
  );
};

export const useUpgradeModal = () => {
  const context = useContext(UpgradeModalContext);
  if (!context) {
    throw new Error('useUpgradeModal must be used within an UpgradeModalProvider');
  }
  return context;
};
